import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { setCredentials } from "../../redux/slices/authSlice";
import { useLazyEndTrialQuery } from "../../redux/slices/paymentApiSlice";
import { fetchUserInfo } from "../../services/authService";
import { createCheckoutSession, createTokenCheckoutSession } from "../../services/subscriptionService";
import stripeConfig from "../stripeConfig";
import PlanCard from "./planCard";
import { getAnalysesPerMonth } from "./priceUtils";

const PlansOptions = ({ isPage, selectSubscription, refetchCurrentSub }) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth).userInfo;
  const token = useSelector((state) => state?.auth?.accessToken);
  const [isYearly, setIsYearly] = useState(true);
  const dispatch = useDispatch();
  const [hoveredButton, setHoveredButton] = useState(null);
  const [endTrialNow, { data, isFetching, error }] = useLazyEndTrialQuery();
  const [standardLoading, setStandardLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [ultimateLoading, setUltimateLoading] = useState(false);
  const { accessToken } = useSelector((state) => state?.auth);

  const STANDARD_PRICE_ID = stripeConfig.priceIds.monthly.standard;
  const ANNUAL_STANDARD_PRICE_ID = stripeConfig.priceIds.yearly.standard;

  const PREMIUM_PRICE_ID = stripeConfig.priceIds.monthly.premium;
  const ANNUAL_PREMIUM_PRICE_ID = stripeConfig.priceIds.yearly.premium;

  const ULTIMATE_PRICE_ID = stripeConfig.priceIds.monthly.ultimate;
  const ANNUAL_ULTIMATE_PRICE_ID = stripeConfig.priceIds.yearly.ultimate;
  const TOKEN_PRICE_ID = stripeConfig.tokenPriceId;
  const stripePromise = loadStripe(stripeConfig.publishableKey);

  // Load User Info
  const loadUserInfo = async () => {
    if (accessToken) {
      try {
        const userInfoResult = await fetchUserInfo();
        dispatch(
          setCredentials({
            userInfo: userInfoResult.data,
            accessToken: accessToken,
          })
        );
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    }
  };

  // CHECKOUT SESSIONS FOR WHEN USER DOES NOT HAVE ANY PLAN
const handleTokenCheckout = async () => {
    try {
      setTokenLoading(true);
      const data = await createTokenCheckoutSession(
        TOKEN_PRICE_ID
      );
      const stripe = await stripePromise;
     
      if (stripe) {
        stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
      }
      setTokenLoading(false);
    } catch (error) {
      setTokenLoading(false);
      console.error("Error during token checkout:", error);
    }
  };
  const handleStandardCheckout = async () => {
    try {
      setStandardLoading(true);
      const data = await createCheckoutSession(
        isYearly ? ANNUAL_STANDARD_PRICE_ID : STANDARD_PRICE_ID
      );
      const stripe = await stripePromise;
      
      if (stripe) {
        stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
      }
      setStandardLoading(false);
    } catch (error) {
      setStandardLoading(false);
      console.error("Error during premium checkout:", error);
    }
  };

  const handlePremiumCheckout = async () => {
    try {
      setPremiumLoading(true);
      const data = await createCheckoutSession(
        isYearly ? ANNUAL_PREMIUM_PRICE_ID : PREMIUM_PRICE_ID
      );
      const stripe = await stripePromise;
      if (stripe) {
        stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
      }
      setPremiumLoading(false);
    } catch (error) {
      setPremiumLoading(false);
      console.error("Error during basic checkout:", error);
    }
  };

  const handleUltimateCheckout = async () => {
    try {
      setUltimateLoading(true);
      const data = await createCheckoutSession(
        isYearly ? ANNUAL_ULTIMATE_PRICE_ID : ULTIMATE_PRICE_ID
      );
      const stripe = await stripePromise;
      if (stripe) {
        stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
      }
      setUltimateLoading(false);
    } catch (error) {
      setUltimateLoading(false);
      console.error("Error during basic checkout:", error);
    }
  };

  const handleEndTrial = async () => {
    const resp = await endTrialNow();

    if (resp?.isSuccess == true) {
      toast.success(t(resp?.data?.message));

      refetchCurrentSub();
      loadUserInfo();
    } else {
      toast.error(t(resp?.error?.data.detail));
    }
  };

  useEffect(() => {
    loadUserInfo();
  }, []);

  const tiers = [
    {
      type: "tokens",
      title: "Jetons SEO",
      price: "79",
      description: t(
        "For-begginers",
        "Pour les rédacteurs débutants et occasionnels"
      ),
      features_list: [
        {
          text:
            getAnalysesPerMonth("Jetons SEO") +
            " " +
            t("seo-analysis", "analyses SEO"),
          tooltip:
            "Les jetons achetés ici sont non expirables et peuvent être utilisés à tout moment.",
        },
        {
          text: t(
            "all-in-one-blog-post-editor",
            "Éditeur d'articles de blog tout-en-un"
          ),
        },
        {
          text: t("VIP-customer-support", "Support Client VIP"),
        },
        {
          text: t("private-group-access", "Accès au groupe privé BotBlogR"),
        },
      ],
      features_missing: [
        {
          text: t("draft-blog-posts", "Blog post drafts"),
          tooltip:
            "Le système de brouillon vous permet de commencer à rédiger et même rédiger en entier des articles sans avoir à dépenser de crédit d'analyse SEO.",
        },
      ],
      trial_Label:
        (userInfo.plan === "Standard" &&
          userInfo.payment_status === "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Standard" &&
          userInfo.payment_status === "trial")
          ? true
          : false,
      buttonText: t("buy-tokens", "Acheter des jetons"),
      buttonVariant: "contained",
      loading: tokenLoading,
      disabled:
        (userInfo.plan === "Standard" &&
          userInfo.payment_status != "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Standard" &&
          userInfo.payment_status != "trial")
          ? true
          : tokenLoading,
      onClick: () =>
        // userInfo.plan === null
        //   ? handleStandardCheckout()
        //   : userInfo.payment_status == "trial" && userInfo.plan === "Standard"
        //   ? handleEndTrial()
        //   : isYearly
        //   ? selectSubscription(ANNUAL_STANDARD_PRICE_ID)
        //   : selectSubscription(STANDARD_PRICE_ID),
        handleTokenCheckout(),
    },
    {
      type: "subscription",
      title: "Starter",
      price: isYearly ? "24.00" : "29.00",
      description: t(
        "For-begginers",
        "Pour les rédacteurs débutants et occasionnels"
      ),
      features_list: [
        {
          text:
            getAnalysesPerMonth("Starter") +
            " " +
            t("seo-analysis-per-month", "analyses SEO par mois"),
          tooltip:
            "Les analyses SEO sont renouvelées chaque mois à la date de facturation de votre abonnement.",
        },
        {
          text: t("draft-blog-posts", "Blog post drafts"),
          tooltip:
            "Le système de brouillon vous permet de commencer à rédiger et même rédiger en entier des articles sans avoir à dépenser de crédit d'analyse SEO.",
        },
        {
          text: t(
            "all-in-one-blog-post-editor",
            "Éditeur d'articles de blog tout-en-un"
          ),
        },
        {
          text: t("VIP-customer-support", "Support Client VIP"),
        },
        {
          text: t("private-group-access", "Accès au groupe privé BotBlogR"),
        },
      ],
      trial_Label:
        (userInfo.plan === "Standard" &&
          userInfo.payment_status === "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Standard" &&
          userInfo.payment_status === "trial")
          ? true
          : false,
      buttonText:
        userInfo.payment_status === null || userInfo.payment_status === "trial"
          ? t("choose-subscription", "Choisir cet abonnement")
          : (userInfo.plan === "Standard" &&
              isYearly &&
              userInfo.plan_period === "annual") ||
            (userInfo.plan === "Standard" &&
              !isYearly &&
              userInfo.plan_period === "monthly")
          ? t("current-plan", "Abonnement actuel")
          : t("change-plan", "Changer d'abonnement"),
      buttonVariant: "contained",
      loading: standardLoading,
      disabled:
        (userInfo.plan === "Standard" &&
          userInfo.payment_status != "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Standard" &&
          userInfo.payment_status != "trial")
          ? true
          : standardLoading,
      onClick: () =>
        userInfo.plan === null
          ? handleStandardCheckout()
          : userInfo.payment_status == "trial" && userInfo.plan === "Standard"
          ? handleEndTrial()
          : isYearly
          ? selectSubscription(ANNUAL_STANDARD_PRICE_ID)
          : selectSubscription(STANDARD_PRICE_ID),
    },
    {
      type: "subscription",
      title: "Avancé",
      subheader: "Le plus populaire",
      price: isYearly ? "41.00" : "49.00",
      description: t(
        "For-confirmed-writers",
        "Pour les rédacteurs confirmés et réguliers"
      ),
      features_list: [
        {
          text:
            getAnalysesPerMonth("Avancé") +
            " " +
            t("seo-analysis-per-month", "analyses SEO par mois"),
          tooltip:
            "Les analyses SEO sont renouvelées chaque mois à la date de facturation de votre abonnement.",
        },
        {
          text: t("draft-blog-posts", "Blog post drafts"),
          tooltip:
            "Le système de brouillon vous permet de commencer à rédiger et même rédiger en entier des articles sans avoir à dépenser de crédit d'analyse SEO.",
        },
        {
          text: t(
            "all-in-one-blog-post-editor",
            "Éditeur d'articles de blog tout-en-un"
          ),
        },
        {
          text: t("VIP-customer-support", "Support Client VIP"),
        },
        {
          text: t("private-group-access", "Accès au groupe privé BotBlogR"),
        },
      ],
      trial_Label:
        (userInfo.plan === "Premium" &&
          userInfo.payment_status === "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Premium" &&
          userInfo.payment_status === "trial")
          ? true
          : false,
      buttonText:
        userInfo.payment_status === null || userInfo.payment_status === "trial"
          ? t("choose-subscription", "Choisir cet abonnement")
          : (userInfo.plan === "Premium" &&
              isYearly &&
              userInfo.plan_period === "annual") ||
            (userInfo.plan === "Premium" &&
              !isYearly &&
              userInfo.plan_period === "monthly")
          ? t("current-plan", "Abonnement actuel")
          : t("change-plan", "Changer d'abonnement"),
      buttonVariant: "contained",
      loading: premiumLoading,
      disabled:
        (userInfo.plan === "Premium" &&
          userInfo.payment_status != "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Premium" &&
          userInfo.payment_status != "trial")
          ? true
          : premiumLoading,
      onClick: () =>
        userInfo.plan === null
          ? handlePremiumCheckout()
          : userInfo.payment_status == "trial" && userInfo.plan === "Premium"
          ? handleEndTrial()
          : isYearly
          ? selectSubscription(ANNUAL_PREMIUM_PRICE_ID)
          : selectSubscription(PREMIUM_PRICE_ID),
    },
    {
      type: "subscription",
      title: "Expert",
      price: isYearly ? "100.00" : "119.00",
      description: t(
        "For-professionals",
        "Pour les rédacteurs professionnels et webmaster"
      ),
      features_list: [
        {
          text:
            getAnalysesPerMonth("Expert") +
            " " +
            t("seo-analysis-per-month", "analyses SEO par mois"),
          tooltip:
            "Les analyses SEO sont renouvelées chaque mois à la date de facturation de votre abonnement.",
        },
        {
          text: t("draft-blog-posts", "Blog post drafts"),
          tooltip:
            "Le système de brouillon vous permet de commencer à rédiger et même rédiger en entier des articles sans avoir à dépenser de crédit d'analyse SEO.",
        },
        {
          text: t(
            "all-in-one-blog-post-editor",
            "Éditeur d'articles de blog tout-en-un"
          ),
        },
        {
          text: t("VIP-customer-support", "Support Client VIP"),
        },
        {
          text: t("private-group-access", "Accès au groupe privé BotBlogR"),
        },
      ],
      trial_Label:
        (userInfo.plan === "Ultimate" &&
          userInfo.payment_status === "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Ultimate" &&
          userInfo.payment_status === "trial")
          ? true
          : false,
      buttonText:
        userInfo.payment_status === null || userInfo.payment_status === "trial"
          ? t("choose-subscription", "Choisir cet abonnement")
          : (userInfo.plan === "Ultimate" &&
              isYearly &&
              userInfo.plan_period === "annual") ||
            (userInfo.plan === "Ultimate" &&
              !isYearly &&
              userInfo.plan_period === "monthly")
          ? t("current-plan", "Abonnement actuel")
          : t("change-plan", "Changer d'abonnement"),
      buttonVariant: "contained",
      loading: ultimateLoading,
      disabled:
        (userInfo.plan === "Ultimate" &&
          userInfo.payment_status != "trial" &&
          isYearly &&
          userInfo.plan_period === "annual") ||
        (!isYearly &&
          userInfo.plan_period === "monthly" &&
          userInfo.plan === "Ultimate" &&
          userInfo.payment_status != "trial")
          ? true
          : ultimateLoading,
      onClick: () =>
        userInfo.plan === null
          ? handleUltimateCheckout()
          : userInfo.payment_status == "trial" && userInfo.plan === "Ultimate"
          ? handleEndTrial()
          : isYearly
          ? selectSubscription(ANNUAL_ULTIMATE_PRICE_ID)
          : selectSubscription(ULTIMATE_PRICE_ID),
    },
  ];

  return isFetching ? (
    <div className="flex items-center justify-center mt-5">
      <ClipLoader></ClipLoader>
    </div>
  ) : (
    <div className={`${isPage ? "mt-3" : "mt-1"}`}>
      <div className="flex flex-col justify-center items-center">
        {userInfo.payment_status === null && (
          <p className="text-center mb-4">
            {t(
              "choose-a-subscription-to-start",
              "Choisissez un abonnement pour commencer"
            )}
          </p>
        )}

        <p className="text-center mb-6">
          Choisissez un abonnement réccurent pour créer des articles de blog
          chaque mois à tarif réduit ou achetez un pack de jeton d'analyse SEO
          pour créer des contenus à la demande !
        </p>

        <label className={`flex items-center cursor-pointer relative`}>
          <div
            className={`bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex justify-between p-1 gap-1 sm:w-[30rem] max-sm:w-[25rem] relative`}
          >
            <button
              onClick={() => setIsYearly(true)}
              onMouseEnter={() => setHoveredButton("yearly")}
              onMouseLeave={() => setHoveredButton(null)}
              className={`w-1/2 text-center py-2 px-3 rounded-full transition-all duration-300 ${
                isYearly
                  ? "bg-white text-black"
                  : hoveredButton === "yearly"
                  ? "text-white bg-white/40"
                  : "text-white"
              }`}
            >
              <div>{t("Yearly-billing", "Paiement annuel")}</div>
              <div className="text-xs text-gray-400">
                {t("2-months-free", "2 mois gratuits")}
              </div>
            </button>
            <button
              onClick={() => setIsYearly(false)}
              onMouseEnter={() => setHoveredButton("monthly")}
              onMouseLeave={() => setHoveredButton(null)}
              className={`w-1/2 text-center py-2 px-3 rounded-full transition-all duration-300 ${
                !isYearly
                  ? "bg-white text-black"
                  : hoveredButton === "monthly"
                  ? "text-white bg-white/40"
                  : "text-white"
              }`}
            >
              {t("Monthly-billing", "Paiement mensuel")}
            </button>
          </div>
        </label>
      </div>

      <div className="mt-8 container space-y-12 lg:space-y-0 gap-y-8 lg:grid lg:grid-cols-4 lg:gap-x-8">
        {tiers.map((tier, tierIndex) => (
          <PlanCard
            key={tier.id ?? tierIndex}
            isYearly={isYearly}
            tier={tier}
            tierIndex={tierIndex}
          />
        ))}
      </div>
    </div>
  );
};

export default PlansOptions;
